/**
 * SEO helpers for Gårdsförsäljning hub & region pages.
 */

import type { GardforsaljningProducer } from "@/hooks/useGardforsaljningProducers";

const BASE_URL = "https://farmly.se";

/* ─── Hub page ─── */

export function buildHubTitle(): string {
  return "Gårdsförsäljning i Sverige – hitta gårdar med alkoholförsäljning";
}

export function buildHubDescription(): string {
  return "Upptäck svenska gårdar och producenter med gårdsförsäljning av alkohol. Besök vingårdar, bryggerier och destillerier – provning och försäljning i samband med besök.";
}

/* ─── Region page ─── */

export function buildRegionTitle(regionLabel: string): string {
  return `Gårdsförsäljning i ${regionLabel} – producenter med alkoholförsäljning`;
}

export function buildRegionDescription(regionLabel: string): string {
  return `Hitta producenter med gårdsförsäljning av alkohol i ${regionLabel}. Vingårdar, bryggerier och destillerier med provning och försäljning i samband med besök.`;
}

/* ─── JSON-LD: ItemList ─── */

export function buildItemListJsonLd(
  producers: GardforsaljningProducer[],
  pageUrl: string,
  pageName: string
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: pageName,
    url: pageUrl,
    numberOfItems: producers.length,
    itemListElement: producers.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${BASE_URL}/producer/${p.slug}`,
      name: p.name,
    })),
  };
}

/* ─── JSON-LD: FAQPage ─── */

export interface FaqItem {
  question: string;
  answer: string;
}

export function buildFaqJsonLd(items: FaqItem[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

/* ─── FAQ content ─── */

export const GARDFORSALJNING_FAQ: FaqItem[] = [
  {
    question: "Vad är gårdsförsäljning?",
    answer:
      "Gårdsförsäljning innebär att en producent säljer sina egna alkoholprodukter direkt till besökare på plats, i samband med ett besök, en provning eller en visning. Det regleras av svensk lag och kräver tillstånd.",
  },
  {
    question: "Får man köpa alkohol direkt på en gård i Sverige?",
    answer:
      "Ja, under vissa förutsättningar. Producenter med tillstånd för gårdsförsäljning får sälja sina egna produkter till besökare i samband med ett besök eller en provning. Det gäller vin, öl, cider och sprit som producerats på plats.",
  },
  {
    question: "Vilka produkter kan man köpa vid gårdsförsäljning?",
    answer:
      "Produkter som producenten själv tillverkat – exempelvis vin, öl, cider, fruktvin eller destillat. Sortimentet varierar mellan olika producenter.",
  },
  {
    question: "Måste man boka tid för gårdsförsäljning?",
    answer:
      "Det beror på producenten. Vissa erbjuder drop-in under öppettider, medan andra kräver att du bokar en provning eller ett besök i förväg. Kolla med varje producent för aktuell information.",
  },
  {
    question: "Vad är skillnaden mellan gårdsbutik och gårdsförsäljning?",
    answer:
      "En gårdsbutik säljer generella produkter (t.ex. honung, ost, grönsaker) direkt på plats. Gårdsförsäljning avser specifikt försäljning av alkoholprodukter och är ett reglerat begrepp med krav på tillstånd.",
  },
];
